import { useEffect, useState } from 'react';

/** Estilos visuales disponibles (colores, tipografía y radios definidos en index.css con data-tema). */
export const ESTILOS = [
  { id: 'actual', nombre: 'Freisacar', detalle: 'Rojo y negro · Inter', muestras: ['#d42a2a', '#1a1a1a', '#f5f5f5'] },
  { id: 'grafito', nombre: 'Grafito', detalle: 'Azul noche y carmesí · Plus Jakarta Sans', muestras: ['#e11d48', '#0f172a', '#f1f5f9'] },
  { id: 'calido', nombre: 'Cálido', detalle: 'Espresso y rojo ladrillo · Manrope', muestras: ['#b42318', '#2b211c', '#f6f1ea'] },
  { id: 'minimal', nombre: 'Minimal', detalle: 'Plano y compacto · Geist', muestras: ['#dc2626', '#09090b', '#fafafa'] },
  { id: 'luminoso', nombre: 'Luminoso', detalle: 'Menú blanco y acento rojo · Plus Jakarta Sans', muestras: ['#dc2626', '#ffffff', '#f6f7f9'] },
  { id: 'azul', nombre: 'Azul', detalle: 'Azul marino y blanco · Plus Jakarta Sans', muestras: ['#2563eb', '#0f2a5f', '#ffffff'] },
] as const;

export type EstiloId = (typeof ESTILOS)[number]['id'];
export type ModoColor = 'claro' | 'oscuro' | 'sistema';

export type Apariencia = { estilo: EstiloId; modo: ModoColor };

const CLAVE = 'freisacar.apariencia';
const POR_DEFECTO: Apariencia = { estilo: 'actual', modo: 'claro' };

function leerApariencia(): Apariencia {
  try {
    const guardada = JSON.parse(window.localStorage.getItem(CLAVE) || 'null') as Partial<Apariencia> | null;
    const estilo = ESTILOS.some(opcion => opcion.id === guardada?.estilo) ? (guardada?.estilo as EstiloId) : POR_DEFECTO.estilo;
    const modo: ModoColor = guardada?.modo === 'oscuro' || guardada?.modo === 'sistema' ? guardada.modo : 'claro';
    return { estilo, modo };
  } catch {
    return POR_DEFECTO;
  }
}

function sistemaPrefiereOscuro(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function aplicar(apariencia: Apariencia | null) {
  const raiz = document.documentElement;
  if (!apariencia || apariencia.estilo === 'actual') delete raiz.dataset.tema;
  else raiz.dataset.tema = apariencia.estilo;
  const oscuro = Boolean(apariencia) && (apariencia!.modo === 'oscuro' || (apariencia!.modo === 'sistema' && sistemaPrefiereOscuro()));
  raiz.classList.toggle('oscuro', oscuro);
  raiz.style.colorScheme = oscuro ? 'dark' : '';
}

/**
 * Apariencia personalizable del panel. Mientras el panel está montado aplica la preferencia guardada
 * en este navegador; al salir (cerrar sesión) restaura el aspecto por defecto.
 */
export function useApariencia(habilitada: boolean) {
  const [apariencia, setApariencia] = useState<Apariencia>(() => (habilitada ? leerApariencia() : POR_DEFECTO));

  useEffect(() => {
    if (!habilitada) return undefined;
    aplicar(apariencia);
    try {
      window.localStorage.setItem(CLAVE, JSON.stringify(apariencia));
    } catch {
      // Sin almacenamiento: la preferencia dura solo esta sesión.
    }
    if (apariencia.modo !== 'sistema' || typeof window.matchMedia !== 'function') return undefined;
    const consulta = window.matchMedia('(prefers-color-scheme: dark)');
    const alCambiar = () => aplicar(apariencia);
    consulta.addEventListener('change', alCambiar);
    return () => consulta.removeEventListener('change', alCambiar);
  }, [habilitada, apariencia]);

  useEffect(() => (habilitada ? () => aplicar(null) : undefined), [habilitada]);

  return {
    apariencia,
    cambiarEstilo: (estilo: EstiloId) => setApariencia(actual => ({ ...actual, estilo })),
    cambiarModo: (modo: ModoColor) => setApariencia(actual => ({ ...actual, modo })),
  };
}
